import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import fetchSalon from 'actions/fetchSalon.action';
import Button from 'components/form/Button.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import S from 'constants/string.const';
import { withTheme } from 'providers/Theme.provider';
import { formatDayToYearShorter } from 'utils/date/format.util';

import PopupModalContainer from 'common/PopupModal';
import { bookingLogCSV, clearAllErrorMessages, deletedBookingLogCSV, transactionLogCSV } from 'reports/actions';
import ClientsLayout from 'reports/DownloadableReports/clients/mappers/ClientsLayoutContainer';
import PayoutLog from 'reports/DownloadableReports/PayoutLog.component';

import CN from './DownloadableReportsLayout.module.scss';

const HELP_LINK = 'https://help.getslick.com/en/articles/3514031-sms-marketing-overview';

class DownloadableReportsLayout extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: { currentlyLoading: false, loaded: true },
    };
  }

  UNSAFE_componentWillMount() {
    this.props.fetchSalon(this.props.match.params.id);
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.match.params.id !== nextProps.match.params.id) {
      this.props.fetchSalon(nextProps.match.params.id);
      this.props.history.push(`/salon/${nextProps.match.params.id}/reports/main`);
    }
  }

  onDownloadBtnClicked = (type) => {
    if (this.props.lastDatesSelected) {
      switch (type) {
        case 'Transaction': {
          this.props.transactionLogCSV(
            this.props.match.params.id,
            this.props.lastDatesSelected.fromDate,
            this.props.lastDatesSelected.toDate,
          );
          break;
        }
        case 'Booking': {
          this.props.bookingLogCSV(
            this.props.match.params.id,
            this.props.lastDatesSelected.fromDate,
            this.props.lastDatesSelected.toDate,
          );
          break;
        }

        case 'Deleted': {
          this.props.deletedBookingLogCSV(
            this.props.match.params.id,
            this.props.lastDatesSelected.fromDate,
            this.props.lastDatesSelected.toDate,
          );
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  onGoButtonClicked = (fromDate, toDate) => {
    this.setState({ loading: { currentlyLoading: false, loaded: true, dateRange: { from: fromDate, to: toDate } } });
  };

  getErrorToShow = () => {
    if (this.props.transactionLogProgress.errorMessage) return this.props.transactionLogProgress.errorMessage;
    if (this.props.bookingLogProgress.errorMessage) return this.props.bookingLogProgress.errorMessage;
    if (this.props.deletedBookingLogProgress.errorMessage) return this.props.deletedBookingLogProgress.errorMessage;
    return null;
  };

  hideError = () => {
    this.props.clearAllErrorMessages();
  };

  render() {
    const { isDarkMode } = this.props;

    let transactionTitle = 'Transaction Log';
    let bookingTitle = 'Booking Log';
    let deletedBookingTitle = 'Deleted Booking Log';
    if (this.props.lastDatesSelected) {
      const { fromDate } = this.props.lastDatesSelected;
      const { toDate } = this.props.lastDatesSelected;
      transactionTitle = `Transaction Log from ${formatDayToYearShorter(fromDate)} to ${formatDayToYearShorter(toDate)}`;
      bookingTitle = `Booking Log from ${formatDayToYearShorter(fromDate)} to ${formatDayToYearShorter(toDate)}`;
      deletedBookingTitle = `Deleted Booking Log from ${formatDayToYearShorter(fromDate)} to ${formatDayToYearShorter(
        toDate,
      )}`;
    }

    const errorToShow = this.getErrorToShow();

    return (
      <div
        data-bem="DownloadableReportsLayout"
        className={cn({
          [CN.component]: true,
          [CN.dark]: isDarkMode,
        })}
      >
        {errorToShow && (
          <PopupModalContainer
            popupTitle="Error"
            popupMessage={errorToShow}
            buttonText="Okay"
            buttonCallback={this.hideError}
          />
        )}
        <div className="has-protipbox download">
          <div className={CN.container}>
            <div className={CN.panel}>
              <div className={CN.panelCol}>
                <div className={CN.panelRow}>
                  {S.lamp}
                  <div className={CN.panelText}>
                    <div className={CN.panelTitle}>Pro tip:</div>
                    Downloadable reports are ALWAYS INC TAX. If your accountant requires them to be Net (ex tax) then
                    they will need to do this calculation in Excel.
                  </div>
                </div>
                <div className={CN.panelRow}>
                  {S.lamp}
                  <div className={CN.panelText}>
                    <div className={CN.panelTitle}>Pro tip:</div>
                    Downloading a client list isn’t the easiest way of seeing your clients, head to Marketing to see
                    their last visit, average bill etc
                  </div>
                </div>
                <div className={CN.panelRow}>
                  {S.lamp}
                  <div className={CN.panelText}>
                    <div className={CN.panelTitle}>Split Takings Log:</div>
                    This log is showing stylist and business split takings by payment method, so you can easily work out
                    your business' and your stylist’s cash share.
                  </div>
                </div>
              </div>
              <div className={CN.linkContainer}>
                <ExternalLink link={HELP_LINK} variant="link">
                  <div className={CN.link}>Need Help?</div>
                </ExternalLink>
              </div>
            </div>

            <ClientsLayout />
            <div className={CN.subTitle}>Downloadable Reports</div>
            <div className={CN.downloadContainer}>
              {transactionTitle}
              {this.state.loading.loaded && (
                <Button
                  onClick={() => {
                    this.onDownloadBtnClicked('Transaction');
                  }}
                  variant="primary"
                  disabled={this.props.transactionLogProgress.isFetching}
                >
                  DOWNLOAD
                </Button>
              )}
            </div>

            <div className={CN.downloadContainer}>
              {bookingTitle}
              {this.state.loading.loaded && (
                <Button
                  variant="primary"
                  onClick={() => {
                    this.onDownloadBtnClicked('Booking');
                  }}
                  disabled={this.props.bookingLogProgress.isFetching}
                >
                  DOWNLOAD
                </Button>
              )}
            </div>

            <div className={CN.downloadContainer}>
              {deletedBookingTitle}
              {this.state.loading.loaded && (
                <Button
                  variant="primary"
                  onClick={() => {
                    this.onDownloadBtnClicked('Deleted');
                  }}
                  disabled={this.props.deletedBookingLogProgress.isFetching}
                >
                  DOWNLOAD
                </Button>
              )}
            </div>

            <PayoutLog bgn={this.props.lastDatesSelected?.fromDate} end={this.props.lastDatesSelected?.toDate} />
          </div>
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state) {
  const { reports } = state;
  const { transactionLog, bookingLog, deletedBookingLog, lastDatesSelected } = reports;
  return {
    transactionLogProgress: { isFetching: transactionLog.isFetching, errorMessage: transactionLog.errorMessage },
    bookingLogProgress: { isFetching: bookingLog.isFetching, errorMessage: bookingLog.errorMessage },
    deletedBookingLogProgress: {
      isFetching: deletedBookingLog.isFetching,
      errorMessage: deletedBookingLog.errorMessage,
    },
    lastDatesSelected,
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    transactionLogCSV(salonId, startDate, endDate) {
      dispatch(transactionLogCSV(salonId, startDate, endDate));
    },
    bookingLogCSV(salonId, startDate, endDate) {
      dispatch(bookingLogCSV(salonId, startDate, endDate));
    },
    deletedBookingLogCSV(salonId, startDate, endDate) {
      dispatch(deletedBookingLogCSV(salonId, startDate, endDate));
    },
    fetchSalon(salonId) {
      dispatch(fetchSalon(salonId));
    },
    clearAllErrorMessages() {
      dispatch(clearAllErrorMessages());
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withTheme()(DownloadableReportsLayout));
